import { pipe, map, fromArray, filter, take, subscribe } from "wonka";
import log from "../utils/log";
// caches are defined and managed mainly in the service worker so you should
// check there if making any changes @see js/sw/index.js
import cacheNames from "../sw/cacheNames";

/**
 * URQL exchange to store gql responses in Cache storage (via the CacheAPI)
 * Only runs if service workers (sw) are supported but a sw is not controlling
 * the page. The catches the periods while the sw may be inactive (installing, activating etc)
 *
 */
export default function cacheAPIExchange({ forward }) {
  return (operations$) => {
    // initial exchange setup copied from https://formidable.com/open-source/urql/docs/advanced/authoring-exchanges/
    // We receive a stream of Operations from previous exchange. Streams have `$` at the end of their variable names
    const forwardOperations$ = operations$;

    // function is the next exchange's `ExchangeIO` function
    const operationResult$ = forward(operations$);

    // if sw is supported we're not caching anyway
    // sw support implies support for cache api URLSearchParams
    if (!"serviceWorker" in window.navigator) {
      return operationResult$;
    }

    // if a sw is active, we don't need this so return early
    if (window.navigator.serviceWorker?.controller) {
      return operationResult$;
    }

    // We get back `fetchExchange`'s stream of results, which we inspect
    // for any responses we would like to cache
    return pipe(
      operationResult$,
      map((op) => {
        log.debug("cacheAPIExchange:", op);

        // ignore error responses
        if (op.error) {
          return op;
        }

        // extract enough info to identify the operation
        const {
          operation: { kind = null, query = null },
        } = op;

        // if this isn't a query, then bail
        if (kind !== "query") {
          return op;
        }

        // check for cache api
        const caches = window?.caches;
        if (!caches) {
          return op;
        }

        try {
          // rebuild response here and store in cache storage
          caches.open(cacheNames.graphql).then((cache) => {
            // get operation name from definition (array of gql definitions)
            let operationName = "";
            pipe(
              fromArray(query.definitions),
              // grab operation definition - which should always exist
              filter((def) => def.kind === "OperationDefinition"),
              map((def) => def?.name?.value),
              take(1),
              subscribe((name) => {
                // empty string is also valid if query hasn't been named
                if (!name) {
                  log.error(
                    "cacheAPIExchange: query with no operation name found - consider updating .gql file"
                  );
                  return;
                }

                operationName = name;
              })
            );

            // we know this is an OperationDefinition now so extract specific data
            const {
              operation: {
                context: { url },
                variables,
              },
              data,
            } = op;

            // reconstruct response to store in cache
            const params = new URLSearchParams();

            params.append("operationName", operationName);

            params.append("variables", JSON.stringify(variables));

            const extensions = {
              persistedQuery: {
                sha256Hash: query.id,
                version: 1,
              },
            };
            params.append("extensions", JSON.stringify(extensions));

            const key = `${url}?${params.toString()}`;
            log.debug(`cacheAPIExchange: ${key}`);

            const headers = new Headers({
              "Content-Type": "application/json",
              // make it expire immediately so sw responses replace it
              // content length should never match so
              "Content-Length": "1",
            });

            const body = JSON.stringify({ data });

            const response = new Response(body, {
              status: 200,
              statusText: "OK",
              headers,
            });

            // put even if overwriting as the responses are equivalent and
            // can be updated by the sw
            cache.put(key, response);
          });
        } catch (e) {
          // unsupported environment
          if (e instanceof SecurityError) {
            log.warn(e);
            return op;
          }
        }

        // just pass one results
        return op;
      })
    );
  };
}
